import { LoadingSpinner } from '@/features/help/components/LoadingSpinner';
import { useFormSubmission } from '../../hooks/use-form-submission.hook';
import { useNavigationStore } from '../../store/navigation.store';
import type { FormTheme } from '../../types/forms';
import { getApplicationBrandTheme } from '../../utils/brand-theme.utils';

interface FormNavigationProps {
  activePage: number;
  totalPages: number;
  theme: FormTheme;
  onNext: () => void;
  onBack: () => void;
  mappingId: string | undefined;
}

// Purpose: Handles form navigation and submission process with loading states
export const FormNavigation = ({
  activePage,
  totalPages,
  theme,
  onNext,
  onBack,
  mappingId,
}: FormNavigationProps) => {
  const { handleSubmit, isSubmitting } = useFormSubmission({ mappingShortId: mappingId });
  const isNavigating = useNavigationStore((state) => state.isNavigating);
  const brandTheme = getApplicationBrandTheme(theme);

  const isFirstPage = activePage === 0;
  const isLastPage = activePage === totalPages - 1;
  const primaryButtonStyle = {
    backgroundColor: brandTheme.actionBackground,
    color: brandTheme.actionForeground,
  };

  return (
    <>
      <div className="!flex !flex-row items-center justify-between w-full px-2">
        {/* Back Button */}
        <button
          onClick={onBack}
          disabled={isFirstPage || isNavigating}
          className={`flex items-center justify-center text-sm w-[120px] md:w-[140px] px-6 py-2.5 rounded-full transition-all duration-200 font-medium ${
            isFirstPage || isNavigating
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md border border-gray-300'
          }`}
        >
          <span>Back</span>
        </button>

        {/* Next/Submit Button */}
        {isLastPage ? (
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`flex items-center justify-center text-sm w-[120px] md:w-[140px] px-6 py-2.5 rounded-full transition-all duration-200 font-medium hover:shadow-lg hover:brightness-95 ${
              isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
            }`}
            style={primaryButtonStyle}
          >
            {isSubmitting ? (
              <div className="!flex !flex-row items-center gap-2">
                <LoadingSpinner className="h-5 w-5" />
                <span>Submitting...</span>
              </div>
            ) : (
              <span>Submit</span>
            )}
          </button>
        ) : (
          <button
            onClick={onNext}
            disabled={isNavigating}
            className={`flex items-center justify-center text-sm w-[120px] md:w-[140px] px-6 py-2.5 rounded-full transition-all duration-200 font-medium hover:shadow-lg hover:brightness-95 ${
              isNavigating ? 'opacity-70 cursor-not-allowed' : ''
            }`}
            style={primaryButtonStyle}
          >
            {isNavigating ? (
              <div className="!flex !flex-row items-center gap-2">
                <LoadingSpinner className="h-5 w-5" />
                <span>Saving...</span>
              </div>
            ) : (
              <span>Next</span>
            )}
          </button>
        )}
      </div>
    </>
  );
};
